import { ContentType, CONTENTTYPE_PERMISSIONS_MAP } from '@creditsights/api-ui-web';
import { colors, Icon, ICONS } from '@creditsights/styleguide-web';
import React from 'react';
import { ROUTES } from '../../../../Base.constants';
import { useCustomNavigate } from '../../../../hooks/common';
import useFetchUserPermissions from '../../../../hooks/permissions/useFetchUserPermissions';
import { UseFetchSearchQuery } from '../../../../hooks/searches/useFetchSearch';
import { ArticleTableRow } from '../../../../models/ArticleTableRow';
import ArticlesFeedPaginatedWidget from '../../../Feeds/ArticlesFeedPaginatedWidget';
import { DisplayType } from '../../../Feeds/FeedLeftContainer/DisplayTypeSelection';
import NoSearchResults from '../../NoSearchResults/NoSearchResults';
import { SearchResultCategory } from '../../SearchResults.constants';
import SearchResultTableHeader from '../SearchResultTableHeader/SearchResultTableHeader';
import { SEARCH_RESULT_TABLES_PAGINATION, searchResultsTableConfig } from '../searchResultTables.logic';

interface ArticlesSearchResultTableProps {
  query: UseFetchSearchQuery;
  activeCategory: SearchResultCategory;
  tableCategory: SearchResultCategory;
  onCategorySelect: () => void;
  showItemsCount: number;
  onShowItemsCountChange: (count: number) => void;
  contentType: ContentType;
  page: number;
  setPage: (value: number) => void;
}

const ArticlesSearchResultTable = ({
  contentType,
  query,
  onCategorySelect,
  showItemsCount,
  onShowItemsCountChange,
  activeCategory,
  tableCategory,
  page,
  setPage,
}: ArticlesSearchResultTableProps): React.ReactElement | null => {
  const { navigateOnMouseUp } = useCustomNavigate();
  const { data, isLoading, isError, refetch, totalResults, totalPages } = query;
  const noResults = !isLoading && !data?.length && !isError;
  const isTableCategoryTabActive = activeCategory === tableCategory;
  const shouldDisplayTable =
    isTableCategoryTabActive ||
    activeCategory === SearchResultCategory.All ||
    activeCategory === SearchResultCategory.Titles ||
    activeCategory === SearchResultCategory.Topics;
  const { data: userPermissions } = useFetchUserPermissions();
  const hasPermissions = userPermissions!.permissions.includes(CONTENTTYPE_PERMISSIONS_MAP[contentType]);
  const onArticleClick = (article: ArticleTableRow, event) => {
    navigateOnMouseUp(event, { route: ROUTES.article(article.id) });
  };

  const renderHeaderIcon = () => {
    return !hasPermissions ? <Icon name={ICONS.LockOutline} color={colors.tertiary800} /> : null;
  };

  if (!shouldDisplayTable) {
    return null;
  }

  if (noResults) {
    return isTableCategoryTabActive ? <NoSearchResults /> : null;
  }

  return (
    <ArticlesFeedPaginatedWidget
      viewConfig={searchResultsTableConfig(onArticleClick, userPermissions!)}
      articles={data as ArticleTableRow[]}
      isLoading={isLoading}
      isError={isError}
      onRetryAfterErrorClick={refetch}
      contentType={contentType}
      showItemsCount={showItemsCount}
      onShowItemsCountChange={onShowItemsCountChange}
      filterEnabled
      onShowAllSelected={!isTableCategoryTabActive ? onCategorySelect : undefined}
      displayType={DisplayType.Cozy}
      paginationOptions={
        !isTableCategoryTabActive
          ? SEARCH_RESULT_TABLES_PAGINATION.options
          : SEARCH_RESULT_TABLES_PAGINATION.extendedOptions
      }
      widgetHeader={
        <SearchResultTableHeader title={contentType} totalResults={totalResults} icon={renderHeaderIcon()} />
      }
      collapsable={false}
      page={page}
      fetchPage={setPage}
      totalPages={totalPages}
      isTableCategoryTabActive={isTableCategoryTabActive}
      isAlwaysExpanded
    />
  );
};

export default ArticlesSearchResultTable;
